import React, { useState, useEffect, useMemo } from 'react';
import {
  message,
  Steps,
  Form,
  Tooltip,
  Col,
  Row,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Drawer,
} from 'components/gyramais';
import './styles.less';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { formatError } from 'utils';

import BusinessInfoStep from './components/BusinessInfo';
import AddressForm from './components/AddressForm';
import BusinessDocuments from './components/BusinessDocuments';
import BankAccount from './components/BankAccount';
import BusinessUser from './components/BusinessUser';

import SAVE_ADDRESS from './graphql/saveAddress.gql';
import BUSINESS from './graphql/business.gql';
import SAVE_BUSINESS from './graphql/saveBusiness.gql';
import GET_MY_FLOW from './graphql/getMyFlow.gql';

const { useForm } = Form;

const PendingDrawer = ({
  loan,
  business,
  visible,
  onClose,
  refetch,
}) => {
  const flow = ['voltz'].includes(loan?.portfolio?.ownerValue) ? 'back' : 'front';
  const [form] = useForm();

  const [activeTab, setActiveTab] = useState('info');
  const [
    fetchThisBusiness,
    {
      data: { business: businessCached } = {},
      loading: fetchThisBusinessLoading,
    },
  ] = useLazyQuery(BUSINESS, { fetchPolicy: 'network-only' });

  const {
    data: {
      getMyFlow: {
        businessDocuments = [],
      } = {},
    } = {},
  } = useQuery(GET_MY_FLOW, {
    variables: {
      loanId: loan?.id,
    },
    skip: !loan?.Id,
  });

  const additionalFiles = useMemo(() => {
    const files = {};

    const additionalFiles = business?.additionalFiles ?? [];
    additionalFiles.forEach((file) => {
      const parentName = file.parent?.name;

      if (!parentName) return;

      if (files[parentName]) {
        if (Array.isArray(files[parentName])) {
          files[parentName].push(file);
        } else {
          files[parentName] = [files[parentName], file];
        }
      } else {
        files[parentName] = file;
      }

    });

    return files;
  }, [business]);

  const documents = useMemo(() => businessDocuments.map((document) => ({
    ...document,
    file: additionalFiles[document.documentValue],
  })), [businessDocuments, additionalFiles]);

  const [saveAddress, { loading: addressLoading }] = useMutation(SAVE_ADDRESS, {
    onError: (error) => {
      message.error(formatError(error));
    },
  });

  const [saveBusiness, { loading: businessLoading }] = useMutation(SAVE_BUSINESS, {
    onCompleted: () => {
      setActiveTab('address');
      refetch();
    },
    onError: (error) => {
      message.error(formatError(error));
    },
  });

  const [
    saveBusinessToComplete,
    { loading: businessToCompleteLoading },
  ] = useMutation(SAVE_BUSINESS, {
    onCompleted: () => {
      onClose();
      setActiveTab('address');
      refetch();
    },
    onError: (error) => {
      message.error(formatError(error));
    },
  });

  const loading = addressLoading
    || fetchThisBusinessLoading
    || businessLoading
    || businessToCompleteLoading;

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose();
  };

  const stepList = [
    {
      key: 'info',
      name: 'Informações',
      completed: loan?.portfolio.ownerValue === 'voltz'
        ? (business?.additionalInfo?.socialCapital
          && business?.additionalInfo?.revenue
          && business?.additionalInfo?.requestedAmount
          && business?.additionalInfo?.capitalType
          && business?.additionalInfo?.taxRegime
          && business?.additionalInfo?.legalNature
          && business?.additionalInfo?.activityBranch)
        : (business?.platformType?.value
          && business?.sector?.value
          && business?.segment?.value
          && business?.employeesNumber
          && business?.revenueAmount),
      bodyElement: (
        <BusinessInfoStep
          businessId={business.id}
          form={form}
          loading={loading}
          flow={flow}
          loanId={loan?.id}
        />
      ),
      submitButtonText: 'Salvar',
      onSubmit: async () => {
        saveBusiness({
          variables: {
            id: business.id,
            platformTypeValue: form.getFieldValue('platformType'),
            sectorValue: form.getFieldValue('sector'),
            segmentValue: form.getFieldValue('segment'),
            employeesNumber: Number(form.getFieldValue('employeesNumber')),
            revenueAmount: Number(form.getFieldValue('revenueAmount')),
            requestedAmount: Number(form.getFieldValue('requestedAmount')),
            name: form.getFieldValue('name'),
            legalName: form.getFieldValue('legalName'),
            phoneNumber: form.getFieldValue('phoneNumber'),
            socialCapital: Number(form.getFieldValue('socialCapital')),
            capitalType: form.getFieldValue('capitalType'),
            revenue: Number(form.getFieldValue('revenue')),
            legalNature: form.getFieldValue('legalNature'),
            activityBranch: form.getFieldValue('activityBranch'),
            taxRegime: form.getFieldValue('taxRegime'),
          },
        });
      },
    },
    {
      key: 'address',
      name: 'Endereço',
      completed: business?.address?.id,
      bodyElement: (
        <AddressForm
          businessId={business.id}
          address={business.address ?? undefined}
          form={form}
          loading={loading}
          flow={flow}
        />
      ),
      submitButtonText: 'Salvar',
      onSubmit: async () => {
        await saveAddress({
          variables: {
            postalCode: form.getFieldValue('postalCode'),
            number: form.getFieldValue('number'),
            complement: form.getFieldValue('complement'),
            street: form.getFieldValue('street'),
            district: form.getFieldValue('district'),
            city: form.getFieldValue('city'),
            state: form.getFieldValue('state'),
            id: business.address?.id,
            businessId: business.id,
          },
        });
        await fetchThisBusiness({
          variables: {
            id: business.id,
          },
        });
        setActiveTab('documents');
      },
    }, {
      key: 'documents',
      name: 'Documentos',
      completed: loan?.portfolio.ownerValue === 'voltz' ? documents.every((document) => !!document.file) : (
        businessCached?.bylaws?.id
        && businessCached?.address?.proofOfAddress?.id
        && businessCached?.additionalFiles?.find((currentFile) => currentFile.parent?.name === 'raisPerson')
      ),
      bodyElement: (
        <BusinessDocuments
          loanId={loan?.id}
          flow={flow}
          businessId={business.id}
          loading={loading}
          onFileUpdated={() => fetchThisBusiness({ variables: { id: business.id } })}
        />
      ),
      submitButtonText: 'Próximo',
      onSubmit: async () => {
        setActiveTab('bankAccount');
      },
    }, {
      key: 'bankAccount',
      name: 'Dados bancários',
      completed: businessCached?.bankAccount?.id,
      bodyElement: (
        <BankAccount
          businessId={business.id}
          loading={loading}
          flow={flow}
        />
      ),
      submitButtonText: 'Próximo',
      onSubmit: async () => {
        setActiveTab('businessUsers');
      },
    }, {
      key: 'businessUsers',
      name: 'Sócios',
      completed: businessCached?.users.every((user) => user.completedAt),
      bodyElement: (
        <BusinessUser
          loan={loan}
          businessId={business.id}
          loading={loading}
          flow={flow}
        />
      ),
    },
  ];

  const STEP_MAP = stepList.reduce((acc, item, index) => {
    acc[item.key] = index;
    return acc;
  }, {});

  const currentStep = stepList.find((list) => list.key === activeTab);
  useEffect(() => {
    if (!visible) {
      return;
    }

    fetchThisBusiness({
      variables: {
        id: business.id,
      },
    });
  }, [visible]);
  return (
    <Drawer
      open={visible}
      onClose={handleClose}
      className="pending-drawer"
      width="70%"
      destroyOnClose
      placement="right"
      primaryButton={{
        visible: false,
      }}
      extraFooterComponents={(
        <div className="footer-container">
          {currentStep.onSubmit && (
            <Button
              loading={loading}
              onClick={() => {
                currentStep.onSubmit();
              }}
            >
              {currentStep.submitButtonText}
            </Button>
          )}
          {stepList.every((item) => item.completed) && (
            <Button
              loading={loading}
              onClick={() => {
                saveBusinessToComplete({
                  variables: {
                    id: business.id,
                    completed: true,
                  },
                });
              }}
            >
              Completar empréstimo
            </Button>
          )}

          {!stepList.every((item) => item.completed) && (
            <>
              <Tooltip
                overlayStyle={{ whiteSpace: 'pre-line' }}
                title={`Você poderá completar o empréstimo quando todas as listas estiverem completas.\n 
No momento faltam:
 ${stepList.filter((item) => !item.completed).map((item) => item.name).join(', ')}`}
                trigger="hover"
              >
                <div>
                  <Button
                    disabled
                  >
                    Completar empréstimo
                  </Button>
                </div>

              </Tooltip>
            </>
          )}
        </div>
      )}
    >
      {!business.completedAt && business.pendingDocumentNotes && (
        <Row align="middle" className="pending-document-row" gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12}>
            <span className="pending-document-text">
              Pendências
            </span>
          </Col>
          <Col>
            <div className="notify-pending-document">
              <Tooltip
                title={business.pendingDocumentNotes}
                placement="bottomRight"
                overlayStyle={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
          </Col>
        </Row>
      )}

      <Steps
        current={STEP_MAP[activeTab]}
        onChange={(index) => {
          const key = Object.keys(STEP_MAP)[index];
          setActiveTab(key);
        }}
        items={stepList.map((data) => ({
          title: data.name,
          icon: data.completed ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />,
        }))}
        size="small"
        responsive
      />
      {currentStep?.bodyElement}
    </Drawer>
  );
};

export default PendingDrawer;
